import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import { Plus, Save, ChevronLeft, ChevronRight, BookOpen } from "lucide-react";

const PERIODS = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly" };
const PROMPTS = {
  weekly: [
    { key: "wins", question: "What went well this week?" },
    { key: "friction", question: "What created friction or slowed you down?" },
    { key: "learned", question: "What did you learn?" },
    { key: "next", question: "What's the most important thing for next week?" },
    { key: "drop", question: "What should you stop doing or deprioritize?" },
  ],
  monthly: [
    { key: "progress", question: "How did you move toward your Compass goals?" },
    { key: "commitments", question: "Which commitments did you keep? Which did you break?" },
    { key: "patterns", question: "What patterns are you noticing?" },
    { key: "energy", question: "What drained you most? What energized you?" },
    { key: "next_month", question: "What does success look like next month?" },
  ],
  quarterly: [
    { key: "alignment", question: "How aligned was your work with your mission?" },
    { key: "milestones", question: "What milestones did you hit or miss?" },
    { key: "decisions", question: "What were the most consequential decisions you made?" },
    { key: "drift", question: "Where did you drift from your Compass? Why?" },
    { key: "next_quarter", question: "What would make next quarter transformative?" },
  ],
};

function getPeriodRange(period) {
  const now = new Date();
  if (period === "weekly") {
    const mon = new Date(now); mon.setDate(now.getDate() - now.getDay() + 1);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return [mon.toISOString().slice(0,10), sun.toISOString().slice(0,10)];
  }
  if (period === "monthly") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth()+1, 0);
    return [start.toISOString().slice(0,10), end.toISOString().slice(0,10)];
  }
  const q = Math.floor(now.getMonth()/3);
  const start = new Date(now.getFullYear(), q*3, 1);
  const end = new Date(now.getFullYear(), q*3+3, 0);
  return [start.toISOString().slice(0,10), end.toISOString().slice(0,10)];
}

export default function Review() {
  const navigate = useNavigate();
  const { current } = useWorkspace();
  const [reviews, setReviews] = useState([]);
  const [active, setActive] = useState(null);
  const [period, setPeriod] = useState("weekly");
  const [responses, setResponses] = useState({});
  const [dirty, setDirty] = useState(false);
  const wsId = current?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    try { const { data } = await api.get(`/workspaces/${wsId}/reviews`); setReviews(data); } catch {}
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const openReview = (r) => { setActive(r); setResponses(r.responses || {}); setDirty(false); };

  const newReview = async () => {
    const [from, to] = getPeriodRange(period);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/reviews`, { period, date_from: from, date_to: to, responses: {} });
      setReviews(r => [data, ...r]);
      openReview(data);
    } catch { toast.error("Failed"); }
  };

  const save = async () => {
    if (!active || !wsId) return;
    try {
      const { data } = await api.put(`/workspaces/${wsId}/reviews/${active.review_id}`, { responses });
      setReviews(r => r.map(x => x.review_id===data.review_id?data:x));
      setActive(data); setDirty(false);
      toast.success("Review saved");
    } catch { toast.error("Failed"); }
  };

  const prompts = PROMPTS[active?.period || "weekly"];

  return (
    <div className="flex h-full bg-[#0A0A0A]">
      {/* Sidebar */}
      <div className="w-56 flex-shrink-0 border-r border-[#1A1A1A] flex flex-col">
        <div className="px-4 py-4 border-b border-[#1A1A1A]">
          <button onClick={() => navigate("/")} className="flex items-center gap-1 text-[10px] text-[#71717A] hover:text-[#A1A1AA] transition-colors mb-2">
            <ChevronLeft className="w-3 h-3" /> Overview
          </button>
          <h2 className="text-[12px] font-semibold text-[#71717A] uppercase tracking-wider mb-3">Review</h2>
          <div className="flex flex-col gap-1.5 mb-3">
            {Object.entries(PERIODS).map(([k,v]) => (
              <button key={k} onClick={() => setPeriod(k)}
                className={`px-3 py-1.5 rounded-lg text-[11px] border text-left transition-colors ${period===k?"border-[#5A90F5]/40 bg-[#5A90F5]/10 text-[#5A90F5]":"border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA]"}`}>
                {v}
              </button>
            ))}
          </div>
          <button onClick={newReview} className="w-full flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#5A90F5] hover:bg-[#4A80E5] text-white text-[11px] font-semibold justify-center">
            <Plus className="w-3.5 h-3.5"/> New {PERIODS[period]}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {reviews.map(r => (
            <button key={r.review_id} onClick={() => openReview(r)}
              className={`w-full text-left px-4 py-2.5 hover:bg-[#0F0F0F] transition-colors ${active?.review_id===r.review_id?"bg-[#141414]":""}`}>
              <p className="text-[11px] font-medium text-[#A1A1AA] capitalize">{r.period}</p>
              <p className="text-[10px] text-[#3A3A3A]">{r.date_from} → {r.date_to.slice(5)}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 overflow-y-auto">
        {!active ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <BookOpen className="w-10 h-10 text-[#2A2A2A]"/>
            <p className="text-[13px] text-[#3A3A3A]">Select a period and start a review</p>
          </div>
        ) : (
          <div className="px-8 py-6 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-[18px] font-bold text-[#EDEDED] capitalize">{active.period} Review</h2>
                <p className="text-[12px] text-[#52525B]">{active.date_from} to {active.date_to}</p>
              </div>
              <button onClick={save} disabled={!dirty}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-semibold transition-colors disabled:opacity-30"
                style={{ background: dirty?"#5A90F5":"#1A1A1A", color: dirty?"#fff":"#52525B" }}>
                <Save className="w-3.5 h-3.5"/> Save
              </button>
            </div>
            <div className="space-y-6">
              {prompts.map(p => (
                <div key={p.key}>
                  <label className="block text-[12px] font-semibold text-[#EDEDED] mb-2">{p.question}</label>
                  <textarea value={responses[p.key]||""} onChange={e=>{setResponses(r=>({...r,[p.key]:e.target.value}));setDirty(true)}}
                    placeholder="Write your answer..."
                    className="w-full bg-[#111] border border-[#2A2A2A] rounded-xl px-4 py-3 text-[13px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none resize-none focus:border-[#5A90F5]/50 transition-colors leading-relaxed"
                    rows={3}/>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
